/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  ButtonBase,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import {
  AGUARDANDO_INSPECAO,
  EM_ANALISE,
  EM_INSPECAO,
  INSPECAO_EM_ENVIO,
  INSPECAO_ENVIADA_PARCIAL,
  INSPECAO_ENVIADA_COM_SUCESSO,
  INSPECAO_FINALIZADA,
} from '../../../constants/status';
import {
  KPI_CARD_GROUPS,
  KPI_KEY_ERRO_INSPECAO,
  STATUS_COLOR,
  getGroupedMetric,
  sumMetrics,
} from './constants';

const TOTAL_KEY = 'TOTAL';

const KPI_ICONS = {
  [EM_ANALISE]: <AssignmentOutlinedIcon />,
  [AGUARDANDO_INSPECAO]: <HourglassEmptyIcon />,
  [EM_INSPECAO]: <SearchIcon />,
  [INSPECAO_EM_ENVIO]: <CloudUploadOutlinedIcon />,
  [INSPECAO_ENVIADA_PARCIAL]: <CallSplitIcon />,
  [KPI_KEY_ERRO_INSPECAO]: <ErrorOutlineIcon />,
  [INSPECAO_FINALIZADA]: <CheckCircleOutlineIcon />,
  [INSPECAO_ENVIADA_COM_SUCESSO]: <TaskAltIcon />,
};

const CARDS = [
  { key: TOTAL_KEY, label: 'Total', icon: <FormatListBulletedIcon /> },
  ...KPI_CARD_GROUPS.map((group) => ({
    key: group.key,
    label: group.label,
    statuses: group.statuses,
    color: group.color,
    icon: KPI_ICONS[group.key] || <FormatListBulletedIcon />,
  })),
];

/** Linha 1: total + fases iniciais do pipeline. Linha 2: envio, erros e conclusão. */
const KPI_ROWS = [CARDS.slice(0, 5), CARDS.slice(5)];

const ROW_DIVIDER_SX = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'stretch',
  '& > *': {
    flex: 1,
    minWidth: { xs: '50%', sm: 160, md: 0 },
  },
  '& > *:not(:first-of-type)': {
    borderLeft: { sm: 1 },
    borderColor: 'divider',
  },
};

const resolveColor = (theme, colorKey) => {
  if (colorKey === 'default' || !theme.palette[colorKey]) {
    return theme.palette.text.secondary;
  }
  return theme.palette[colorKey].main;
};

const formatNumber = (n) => (n ?? 0).toLocaleString('pt-BR');

const KpiCell = ({ config, metric, active, onClick }) => {
  const colorKey =
    config.key === TOTAL_KEY ? STATUS_COLOR.TOTAL : config.color || STATUS_COLOR[config.key] || 'default';
  const label = config.label;

  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        width: '100%',
        justifyContent: 'flex-start',
        textAlign: 'left',
        px: 2,
        py: 1.75,
        borderRadius: 0,
        transition: 'background-color 0.2s ease',
        backgroundColor: (theme) =>
          active ? `${resolveColor(theme, colorKey)}14` : 'transparent',
        '&:hover': {
          backgroundColor: (theme) => `${resolveColor(theme, colorKey)}0F`,
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: (theme) => resolveColor(theme, colorKey),
            backgroundColor: (theme) => `${resolveColor(theme, colorKey)}1F`,
            border: (theme) =>
              active ? `2px solid ${resolveColor(theme, colorKey)}` : '2px solid transparent',
          }}
        >
          {React.cloneElement(config.icon, { sx: { fontSize: 20 } })}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.8125rem' }}
            noWrap
            title={label}
          >
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {formatNumber(metric.nsirs)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            NSIRs
          </Typography>
        </Box>
      </Stack>
    </ButtonBase>
  );
};

KpiCell.propTypes = {
  config: PropTypes.object.isRequired,
  metric: PropTypes.shape({ nsirs: PropTypes.number }).isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

KpiCell.defaultProps = {
  active: false,
};

const KpiSkeleton = () => (
  <Box sx={{ px: 2, py: 1.75 }}>
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Skeleton variant="circular" width={40} height={40} />
      <Box sx={{ flex: 1 }}>
        <Skeleton width="70%" height={18} />
        <Skeleton width="40%" height={28} />
      </Box>
    </Stack>
  </Box>
);

const renderCard = (config, filtrado, activeStatus, onSelectStatus) => {
  const metric =
    config.key === TOTAL_KEY
      ? sumMetrics(filtrado)
      : getGroupedMetric(filtrado, config.statuses);
  const isTotal = config.key === TOTAL_KEY;
  const active = isTotal ? activeStatus == null : activeStatus === config.key;

  return (
    <KpiCell
      key={config.key}
      config={config}
      metric={metric}
      active={active}
      onClick={() => {
        if (isTotal) {
          onSelectStatus(null);
        } else {
          onSelectStatus(activeStatus === config.key ? null : config.key);
        }
      }}
    />
  );
};

const KpiStrip = ({ resumo, loading, activeStatus, onSelectStatus }) => {
  const { filtrado } = resumo;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {KPI_ROWS.map((row, rowIndex) => (
        <Box
          key={row.map((c) => c.key).join('-')}
          sx={{
            ...ROW_DIVIDER_SX,
            ...(rowIndex > 0 && {
              borderTop: 1,
              borderColor: 'divider',
            }),
          }}
        >
          {loading
            ? row.map((config) => <KpiSkeleton key={config.key} />)
            : row.map((config) => renderCard(config, filtrado, activeStatus, onSelectStatus))}
        </Box>
      ))}
    </Paper>
  );
};

KpiStrip.propTypes = {
  resumo: PropTypes.shape({
    filtrado: PropTypes.object,
    total: PropTypes.object,
  }).isRequired,
  loading: PropTypes.bool,
  activeStatus: PropTypes.string,
  onSelectStatus: PropTypes.func.isRequired,
};

KpiStrip.defaultProps = {
  loading: false,
  activeStatus: null,
};

export default KpiStrip;
